import React, { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useFieldDependencies } from "@/hooks/useFieldDependencies";
import { MetaFieldEditModal } from "../../../MetaFieldEditModal";
import { formatShopifyFieldValue, type FormattedValue } from "../../../utils/shopifyFormatting";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { useResolveFieldLabel } from "@/lib/views/labels";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation, getUITranslation } from "@/lib/views/labels/translations";
import { Switch } from "@/components/ui/switch";
import { deriveFieldGroupsFromOrderedFields, type FieldGroup } from "@/lib/views/utils/deriveFieldGroups";
import { ChildTablePreview } from "@/components/child-table/ChildTablePreview";
import { useDetailDensity } from "../../../../hooks/useDetailDensity";
import { useDetailEditMode } from "../../../../hooks/useDetailEditMode";
import { useInlineFieldSave } from "@/components/inline-edit/detail/useInlineFieldSave";
import { useBufferFieldSave } from "@/components/inline-edit/detail/useBufferFieldSave";
import { useInlineFieldUndo } from "@/components/inline-edit/detail/useInlineFieldUndo";
import { getAdjacentField } from "@/components/inline-edit/detail/getAdjacentField";
import { InlineFieldRow } from "../../../InlineFieldRow";
import type { DocFieldSchema } from "@/lib/erp/meta";

/**
 * Helper to render FormattedValue (handles both string and HTML content)
 */
function renderFormattedValue(value: FormattedValue): React.ReactNode {
  if (typeof value === "object" && "__html" in value) {
    return <span dangerouslySetInnerHTML={value} />;
  }
  return <>{value}</>;
}

/**
 * Generic Section Widget
 *
 * Fallback widget for sections that don't have custom rendering.
 * Supports both modal (default) and inline editing modes.
 */
export function GenericSection({
  section,
  formData,
  onFieldChange,
  disabled,
  onSave,
  onSubmit,
  isSaving,
  metaFields,
  isNew: _isNew,
  detailKey,
  fieldErrors,
  onClearFieldError,
}: ShopifySectionWidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<FieldGroup | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [modalField, setModalField] = useState<DocFieldSchema | null>(null);
  const { rowPx, rowPy, rowFirst, rowGap, dividerMx, rowLabel } = useDetailDensity();

  // Edit mode: mobile forces modal (built into hook)
  const { editMode } = useDetailEditMode();
  const isNew = !formData?.name;

  // Get view context for label resolution
  const { doctype, mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();
  const { language } = useLanguage();

  // Inline save: network patch for existing docs, local buffer for new docs
  const resolvedDetailKey = detailKey ?? [doctype.toLowerCase(), formData?.name ?? ""];
  const inlineSave = useInlineFieldSave({
    doctype,
    docname: formData?.name ?? "",
    detailKey: resolvedDetailKey,
  });
  const bufferSave = useBufferFieldSave();
  const { saveField, getFieldState, retryField, undoField, getUndoable, lastSavedField } = isNew ? bufferSave : inlineSave;

  useInlineFieldUndo({ undoField, getUndoable, lastSavedField });

  // Translate section label
  const translatedSectionLabel = useMemo(() => {
    return section.label ? getSectionTitleTranslation(section.label, language) : null;
  }, [section.label, language]);

  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      general: getUI("general", "General"),
      editFields: getUI("modal_edit_fields", "Edit Fields"),
      noData: getUI("no_data", "No data"),
      clickToAdd: getUI("click_to_add", "Click to add"),
      notSet: getUI("not_set", "Not set"),
    };
  }, [language]);

  // Get all fields from section columns
  const allFields = useMemo(
    () => section.columns.flatMap((col) => col.fields),
    [section.columns]
  );

  // Compute field dependencies
  const { visibleFieldNames, fieldRequired } = useFieldDependencies(
    allFields,
    formData
  );

  const fieldsToRender = allFields.filter((field) =>
    visibleFieldNames.has(field.fieldname)
  );

  const filteredFields = fieldsToRender.filter(
    (field) => field.fieldname !== 'name'
  );

  const currency = formData.currency || 'THB';

  const groups = useMemo(() => {
    if (filteredFields.length === 0) return [];
    let derivedGroups: FieldGroup[];
    if (metaFields && metaFields.length > 0) {
      derivedGroups = deriveFieldGroupsFromOrderedFields({
        metaFields,
        orderedFields: filteredFields,
        defaultGroupLabel: uiLabels.general,
      });
    } else {
      derivedGroups = [{
        key: "single",
        label: translatedSectionLabel || uiLabels.editFields,
        fields: filteredFields,
      }] satisfies FieldGroup[];
    }

    if (derivedGroups.length <= 1) return derivedGroups;

    const merged: FieldGroup[] = [];
    let currentGroup: FieldGroup | null = null;

    for (const group of derivedGroups) {
      const groupLabel = getSectionTitleTranslation(group.label, language);
      if (!currentGroup) {
        currentGroup = { ...group };
      } else {
        const currentLabel = getSectionTitleTranslation(currentGroup.label, language);
        if (currentLabel.toLowerCase() === groupLabel.toLowerCase()) {
          currentGroup = {
            ...(currentGroup as any),
            fields: [...currentGroup.fields, ...group.fields],
            key: `${currentGroup.key}-merged-${group.key}`,
          };
        } else {
          merged.push(currentGroup);
          currentGroup = { ...group };
        }
      }
    }
    if (currentGroup) merged.push(currentGroup);
    return merged;
  }, [metaFields, filteredFields, translatedSectionLabel, uiLabels.general, uiLabels.editFields, language]);

  const handleInlineCommit = async (fieldname: string, newValue: unknown) => {
    const previousValue = formData[fieldname];
    setEditingField(null);
    onFieldChange(fieldname, newValue);
    await saveField(fieldname, newValue, previousValue);
  };

  // handleAdvance scoped to a group's fields
  const handleAdvance = useCallback((groupFields: DocFieldSchema[], fieldname: string, direction: "next" | "prev") => {
    const next = getAdjacentField(groupFields, fieldname, direction);
    if (next) setEditingField(next);
  }, []);

  // Empty state
  if (groups.length === 0) {
    return (
      <>
        <div
          className={cn(
            CARD,
            !disabled && "cursor-pointer hover:bg-muted/50 transition-colors",
            disabled && "cursor-not-allowed"
          )}
          onClick={() => {
            if (disabled) return;
            setSelectedGroup({
              key: "empty",
              label: translatedSectionLabel || section.label || uiLabels.editFields,
              fields: allFields,
            });
            setModalOpen(true);
          }}
        >
          <div className="px-4 py-5 text-center">
            <div className="text-muted-foreground text-sm">{uiLabels.noData}</div>
            {!disabled && (
              <div className="text-xs text-muted-foreground/70 mt-1">{uiLabels.clickToAdd}</div>
            )}
          </div>
        </div>

        <MetaFieldEditModal
          open={modalOpen}
          onOpenChange={(open) => {
            setModalOpen(open);
            if (!open) setSelectedGroup(null);
          }}
          title={translatedSectionLabel || section.label || uiLabels.editFields}
          fields={allFields}
          section={undefined}
          formData={formData}
          onFieldChange={onFieldChange}
          onSubmit={onSubmit}
          onSave={onSave}
          disabled={disabled}
          isSaving={isSaving}
          documentName={formData?.name}
        />
      </>
    );
  }

  const showGrouped = groups.length > 1;

  const shouldShowGroupLabel = (group: FieldGroup) => {
    if (!showGrouped) return false;
    const groupLabel = getSectionTitleTranslation(group.label, language);
    const sectionLabel = translatedSectionLabel;
    return groupLabel !== sectionLabel;
  };

  return (
    <>
      <div className="space-y-6">
        {(showGrouped ? groups : [groups[0]]).map((group, groupIndex) => (
          // Only pad the first group's top when it actually shows a label. Without a
          // label (e.g. a single-group section like the Customer overview) this pt-2
          // was a stray 8px offset that pushed the left column's first card below the
          // flush right-sidebar card, breaking the two-column start alignment.
          <div key={group.key} className={cn("space-y-3", groupIndex === 0 && shouldShowGroupLabel(group) && "pt-2")}>
            {shouldShowGroupLabel(group) && (
              <h4 className="text-sm font-semibold text-muted-foreground tracking-wide pl-4 pt-2">
                {getSectionTitleTranslation(group.label, language)}
              </h4>
            )}

            <div
              className={cn(
                CARD,
                editMode === "modal" && !disabled && "cursor-pointer",
                disabled && "cursor-not-allowed"
              )}
              onClick={
                editMode === "modal"
                  ? () => {
                      if (disabled) return;
                      setSelectedGroup(group);
                      setModalOpen(true);
                    }
                  : undefined
              }
            >
              <div className="space-y-0">
                {group.fields.map((field, index) => {
                  const value = formData[field.fieldname];
                  const isTable = field.fieldtype === 'Table' || field.fieldtype === 'Table MultiSelect';
                  const isRequired = fieldRequired[field.fieldname] ?? field.reqd === 1;
                  const resolvedLabel = resolveLabel(doctype, field, mode, style, enableSystemTranslations);
                  const isFirst = index === 0;
                  const isLast = index === group.fields.length - 1;

                  if (editMode === "inline") {
                    return (
                      <InlineFieldRow
                        key={field.fieldname}
                        field={field}
                        value={value}
                        label={resolvedLabel.label}
                        editMode={editMode}
                        isEditing={editingField === field.fieldname}
                        onStartEdit={() => setEditingField(field.fieldname)}
                        onCommit={(v) => handleInlineCommit(field.fieldname, v)}
                        onCancel={() => setEditingField(null)}
                        saveState={getFieldState(field.fieldname)}
                        currency={currency}
                        tooltip={field.description}
                        fullWidth={isTable}
                        onModalClick={isTable ? () => setModalField(field) : undefined}
                        onAdvance={(dir) => handleAdvance(group.fields, field.fieldname, dir)}
                        onRetry={() => retryField(field.fieldname)}
                        error={fieldErrors?.[field.fieldname]}
                        onClearError={() => onClearFieldError?.(field.fieldname)}
                        rowPx={rowPx}
                        rowPy={isFirst ? rowFirst : rowPy}
                        rowGap={rowGap}
                        rowLabel={rowLabel}
                        dividerMx={dividerMx}
                        isLast={isLast}
                        isRequired={isRequired}
                      >
                        {isTable && (
                          <ChildTablePreview
                            field={field}
                            value={value}
                            currency={currency}
                            maxRows={3}
                            maxFields={3}
                          />
                        )}
                      </InlineFieldRow>
                    );
                  }

                  // Modal mode: existing rendering
                  const isCheck = field.fieldtype === 'Check';
                  let displayValue: React.ReactNode;
                  let formattedValue: FormattedValue | null = null;
                  if (isTable) {
                    displayValue = (
                      <ChildTablePreview field={field} value={value} currency={currency} maxRows={3} maxFields={3} />
                    );
                  } else if (isCheck) {
                    displayValue = (
                      <Switch checked={value === 1 || value === true} disabled className="pointer-events-none" />
                    );
                  } else {
                    formattedValue = formatShopifyFieldValue(field, value, currency);
                  }

                  return (
                    <React.Fragment key={field.fieldname}>
                      <div
                        className={cn(
                          isTable ? "flex flex-col gap-3" : `flex items-center ${rowGap}`,
                          rowPx,
                          isFirst ? rowFirst : rowPy,
                        )}
                      >
                        <span className={cn(
                          rowLabel, "shrink-0",
                          isTable ? "w-full" : "w-1/2"
                        )} style={{ color: 'rgb(17 24 39 / var(--tw-text-opacity, 1))' }}>
                          {resolvedLabel.label}
                          {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </span>
                        <div className={cn(
                          "text-foreground",
                          isTable ? "w-full" : `${rowLabel} w-1/2 text-right flex items-center justify-end`
                        )}>
                          {isTable || isCheck ? displayValue : <span className="break-words whitespace-pre-wrap">{renderFormattedValue(formattedValue!)}</span>}
                        </div>
                      </div>
                      {!isLast && (
                        <div className={cn(dividerMx, "border-t border-border/40")} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Card-level modal (modal mode or grouped selection) */}
      <MetaFieldEditModal
        open={modalOpen}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) setSelectedGroup(null);
        }}
        title={
          showGrouped
            ? getSectionTitleTranslation(selectedGroup?.label, language)
            : translatedSectionLabel || section.label || "Edit Fields"
        }
        fields={showGrouped ? selectedGroup?.fields : groups[0]?.fields}
        section={undefined}
        formData={formData}
        onFieldChange={onFieldChange}
        onSubmit={onSubmit}
        onSave={onSave}
        disabled={disabled}
        isSaving={isSaving}
        documentName={formData?.name}
      />

      {/* Per-field modal for Table fields in inline mode */}
      {modalField && (
        <MetaFieldEditModal
          open={true}
          onOpenChange={(open) => { if (!open) setModalField(null); }}
          title={resolveLabel(doctype, modalField, mode, style, enableSystemTranslations).label}
          fields={[modalField]}
          section={undefined}
          formData={formData}
          onFieldChange={onFieldChange}
          onSubmit={onSubmit}
          onSave={onSave}
          disabled={disabled}
          isSaving={isSaving}
          documentName={formData?.name}
        />
      )}
    </>
  );
}
